import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Plus, Check, ChevronDown, Trophy, Printer, Share2,
  Layers, Ruler, Shield, MapPin, Footprints, Wrench, Target, Star
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/shared/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { products, categories } from '@/data/products';
import { cn } from '@/lib/utils';

// Specification definitions with icons and descriptions
const specDefinitions = {
  material: {
    label: 'Material',
    icon: Layers,
    description: 'Primary material composition'
  },
  construction: {
    label: 'Construction',
    icon: Wrench,
    description: 'How the product is manufactured'
  },
  backing: {
    label: 'Backing',
    icon: Layers,
    description: 'Base layer material'
  },
  thickness: {
    label: 'Thickness',
    icon: Ruler,
    description: 'Total product height'
  },
  trafficRating: {
    label: 'Traffic Rating',
    icon: Footprints,
    description: 'Recommended foot traffic level'
  },
  recommendedZone: {
    label: 'Recommended Zone',
    icon: MapPin,
    description: 'Best installation location'
  },
  slipResistance: {
    label: 'Slip Resistance',
    icon: Shield,
    description: 'Safety rating for traction'
  },
  installationType: {
    label: 'Installation Type',
    icon: Wrench,
    description: 'How the product is installed'
  },
  bestUseCase: {
    label: 'Best Use Case',
    icon: Target,
    description: 'Ideal application areas'
  }
};

// Category-based specifications
const categorySpecs = {
  "Luxury Vinyl": {
    material: "Luxury Vinyl",
    construction: "Multi-layer composite",
    backing: "Rigid core",
    thickness: "5.5mm - 8mm",
    trafficRating: "Heavy",
    slipResistance: "R10",
    installationType: "Click-lock floating",
    bestUseCase: "Lobbies, corridors, retail spaces"
  },
  "Carpet Tile": {
    material: "Nylon/Polypropylene fiber",
    construction: "Tufted loop pile",
    backing: "Vinyl/Bitumen composite",
    thickness: "6mm - 9mm",
    trafficRating: "Heavy to Extreme",
    slipResistance: "R9",
    installationType: "Adhesive or loose lay",
    bestUseCase: "Offices, lobbies, corridors"
  },
  "Broadloom Rolls & Sheets": {
    material: "Polypropylene fiber",
    construction: "Needle punch",
    backing: "Vinyl backing",
    thickness: "6mm - 8mm",
    trafficRating: "Heavy",
    slipResistance: "R10",
    installationType: "Full adhesive",
    bestUseCase: "Entryways, high-traffic corridors"
  },
  "Custom Logo Mats": {
    material: "Nylon/Rubber composite",
    construction: "Tufted with inlaid design",
    backing: "Nitrile rubber",
    thickness: "8mm - 12mm",
    trafficRating: "Extreme",
    slipResistance: "R11",
    installationType: "Loose lay or recessed",
    bestUseCase: "Building entrances, branding zones"
  },
  "Entry Systems": {
    material: "Aluminum/Vinyl/Carpet",
    construction: "Grid system with inserts",
    backing: "Aluminum frame",
    thickness: "10mm - 22mm",
    trafficRating: "Extreme",
    slipResistance: "R11",
    installationType: "Recessed or surface mount",
    bestUseCase: "Primary entrances, vestibules"
  },
  "Commercial Mats": {
    material: "Vinyl/Rubber",
    construction: "Ribbed or textured surface",
    backing: "Vinyl backing",
    thickness: "3mm - 6mm",
    trafficRating: "Heavy",
    slipResistance: "R10",
    installationType: "Loose lay",
    bestUseCase: "Secondary entrances, service areas"
  },
  "Industrial & Anti-Fatigue": {
    material: "PVC/Rubber compound",
    construction: "Solid or foam core",
    backing: "Anti-slip texture",
    thickness: "9mm - 22mm",
    trafficRating: "Extreme",
    slipResistance: "R12",
    installationType: "Loose lay or interlocking",
    bestUseCase: "Workstations, industrial areas"
  },
  "Recycle Rubber Flooring": {
    material: "Recycled rubber",
    construction: "Vulcanized rubber tiles",
    backing: "Integral rubber",
    thickness: "8mm - 15mm",
    trafficRating: "Extreme",
    slipResistance: "R11",
    installationType: "Adhesive or interlocking",
    bestUseCase: "Gyms, weight rooms, fitness centers"
  },
  "Non-Slip Flooring": {
    material: "Vinyl composite",
    construction: "Textured surface layer",
    backing: "Fiberglass reinforced",
    thickness: "2mm - 4mm",
    trafficRating: "Heavy",
    slipResistance: "R13",
    installationType: "Full adhesive",
    bestUseCase: "Wet areas, ramps, kitchens"
  }
};

// Get specs for a product - uses comparisonSpecs from product data if available
const getProductSpecs = (product) => {
  // First check if product has comparisonSpecs from the Excel data
  if (product.comparisonSpecs) {
    return {
      ...product.comparisonSpecs,
      zones: product.comparisonSpecs.recommendedZone || product.zones?.map(z => `Zone ${z}`).join(', ') || 'Zone 3'
    };
  }
  
  // Fallback to category-based specs
  const baseSpecs = categorySpecs[product.category] || {};
  return {
    ...baseSpecs,
    zones: product.zones?.map(z => `Zone ${z}`).join(', ') || 'Zone 3'
  };
};

// Determine best value for each spec
const determineBest = (productsToCompare, specKey) => {
  if (specKey === 'trafficRating') {
    const ratings = ['Low', 'Low-Medium', 'Medium', 'Medium-High', 'High', 'Heavy', 'Heavy to Extreme', 'Extreme'];
    let bestIdx = -1;
    let bestProductId = null;
    productsToCompare.forEach(p => {
      const specs = getProductSpecs(p);
      const rating = specs.trafficRating || '';
      const idx = ratings.findIndex(r => rating.toLowerCase().includes(r.toLowerCase()));
      if (idx > bestIdx) {
        bestIdx = idx;
        bestProductId = p.id;
      }
    });
    return bestProductId;
  }
  
  if (specKey === 'slipResistance') {
    // Products with "Yes" slip resistance are all equal - don't highlight
    return null;
  }
  
  if (specKey === 'thickness') {
    let maxThickness = 0;
    let bestProductId = null;
    productsToCompare.forEach(p => {
      const specs = getProductSpecs(p);
      const thickness = specs.thickness || '';
      // Try to extract numeric value from thickness string
      const match = thickness.match(/(\d+\.?\d*)\s*(inch|mm|")/i);
      if (match) {
        let value = parseFloat(match[1]);
        // Convert to inches for comparison if in mm
        if (match[2].toLowerCase() === 'mm') {
          value = value / 25.4;
        }
        if (value > maxThickness) {
          maxThickness = value;
          bestProductId = p.id;
        }
      }
    });
    return bestProductId;
  }
  
  return null;
};

const ProductComparisonPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showProductSelector, setShowProductSelector] = useState(false);

  // Initialize from URL params
  useEffect(() => {
    const productSlugs = searchParams.get('products')?.split(',').filter(Boolean) || [];
    const initialProducts = productSlugs
      .map(slug => products.find(p => p.slug === slug))
      .filter(Boolean)
      .slice(0, 4);
    setSelectedProducts(initialProducts);
  }, []);

  // Update URL when products change
  useEffect(() => {
    if (selectedProducts.length > 0) {
      setSearchParams({ products: selectedProducts.map(p => p.slug).join(',') });
    } else {
      setSearchParams({});
    }
  }, [selectedProducts]);

  // Filtered products for selector
  const filteredProducts = useMemo(() => {
    return products
      .filter(p => !p.isPlaceholder)
      .filter(p => !selectedProducts.some(sp => sp.id === p.id))
      .filter(p => categoryFilter === 'all' || p.category === categoryFilter)
      .filter(p => 
        searchQuery === '' || 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
  }, [selectedProducts, categoryFilter, searchQuery]);

  const addProduct = (product) => {
    if (selectedProducts.length < 4) {
      setSelectedProducts([...selectedProducts, product]);
      setShowProductSelector(false);
    }
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    alert('Comparison link copied to clipboard!');
  };

  // Comparison data
  const comparisonSpecs = Object.keys(specDefinitions);
  const bestValues = useMemo(() => {
    if (selectedProducts.length < 2) return {};
    const best = {};
    comparisonSpecs.forEach(spec => {
      best[spec] = determineBest(selectedProducts, spec);
    });
    return best;
  }, [selectedProducts]);

  return (
    <Layout>
      <SEO 
        title="Product Comparison Tool"
        description="Compare up to 4 commercial flooring products side-by-side. Evaluate material, construction, thickness, traffic rating, and installation type to find the perfect flooring solution."
        canonicalUrl="/compare"
      />
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        {/* Hero Section */}
        <section className="bg-primary text-primary-foreground py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Product Comparison Tool
              </h1>
              <p className="text-lg opacity-90">
                Compare up to 4 products side-by-side to find the perfect flooring solution
              </p>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-8">
          {/* Product Selection Slots */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[0, 1, 2, 3].map((index) => {
              const product = selectedProducts[index];
              return (
                <div
                  key={index}
                  className={cn(
                    "relative rounded-xl border-2 border-dashed transition-all duration-200 min-h-[280px]",
                    product ? "border-primary bg-card" : "border-border bg-muted/30 hover:border-primary/50"
                  )}
                >
                  {product ? (
                    <div className="p-4">
                      <button
                        onClick={() => removeProduct(product.id)}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-destructive/10 hover:bg-destructive/20 flex items-center justify-center transition-colors"
                      >
                        <X className="w-4 h-4 text-destructive" />
                      </button>
                      <div className="aspect-[4/3] bg-muted rounded-lg overflow-hidden mb-3">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-semibold text-sm line-clamp-2 mb-1">{product.name}{product.variant ? ` — ${product.variant}` : ''}</h3>
                      <p className="text-xs text-muted-foreground">{product.category}</p>
                      <Link to={`/products/${product.slug}`}>
                        <Button variant="link" size="sm" className="px-0 h-auto text-xs">
                          View Details →
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowProductSelector(true)}
                      disabled={selectedProducts.length >= 4}
                      className="w-full h-full flex flex-col items-center justify-center p-4 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Plus className="w-8 h-8 mb-2" />
                      <span className="text-sm font-medium">Add Product</span>
                      <span className="text-xs">({selectedProducts.length}/4 selected)</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          {selectedProducts.length >= 2 && (
            <div className="flex justify-center gap-4 mb-8">
              <Button variant="outline" onClick={handlePrint} className="gap-2">
                <Printer className="w-4 h-4" />
                Print Comparison
              </Button>
              <Button variant="outline" onClick={handleShare} className="gap-2">
                <Share2 className="w-4 h-4" />
                Share Link
              </Button>
            </div>
          )}

          {/* Comparison Table */}
          {selectedProducts.length >= 2 ? (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 bg-muted/50 font-semibold min-w-[200px]">
                        Specification
                      </th>
                      {selectedProducts.map((product) => (
                        <th key={product.id} className="text-center p-4 bg-muted/50 font-semibold min-w-[180px]">
                          {product.name}{product.variant ? ` — ${product.variant}` : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonSpecs.map((specKey) => {
                      const spec = specDefinitions[specKey];
                      const Icon = spec.icon;
                      const bestProductId = bestValues[specKey];
                      
                      return (
                        <tr key={specKey} className="border-b border-border last:border-0">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Icon className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{spec.label}</p>
                                <p className="text-xs text-muted-foreground">{spec.description}</p>
                              </div>
                            </div>
                          </td>
                          {selectedProducts.map((product) => {
                            const specs = getProductSpecs(product);
                            const value = specs[specKey] || '—';
                            const isBest = bestProductId === product.id;
                            
                            return (
                              <td 
                                key={product.id} 
                                className={cn(
                                  "p-4 text-center",
                                  isBest && "bg-green-50 dark:bg-green-950/20"
                                )}
                              >
                                <div className="flex items-center justify-center gap-2">
                                  {isBest && (
                                    <Trophy className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  )}
                                  <span className={cn(
                                    "text-sm",
                                    isBest && "font-semibold text-green-700 dark:text-green-400"
                                  )}>
                                    {value}
                                  </span>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Select Products to Compare</h3>
                <p className="text-muted-foreground mb-6">
                  Add at least 2 products to see a detailed side-by-side comparison of their specifications and features.
                </p>
                <Button onClick={() => setShowProductSelector(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Products
                </Button>
              </div>
            </div>
          )}

          {/* Legend */}
          {selectedProducts.length >= 2 && (
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-green-600" />
                <span>Best in category</span>
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">Need help choosing?</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/flooring-selector">
                <Button variant="outline" className="gap-2">
                  <Star className="w-4 h-4" />
                  Use Guided Selector
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="gap-2">
                  Browse All Products
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Product Selector Modal */}
        <AnimatePresence>
          {showProductSelector && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowProductSelector(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-card rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Add Product to Compare</h3>
                    <button
                      onClick={() => setShowProductSelector(false)}
                      className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1"
                    />
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.filter(c => c.count > 0).map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="p-4 overflow-y-auto max-h-[60vh]">
                  <div className="grid grid-cols-2 gap-3">
                    {filteredProducts.slice(0, 20).map((product) => (
                      <button
                        key={product.id}
                        onClick={() => addProduct(product)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-muted/50 transition-all text-left"
                      >
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm line-clamp-2">{product.name}{product.variant ? ` — ${product.variant}` : ''}</p>
                          <p className="text-xs text-muted-foreground truncate">{product.category}</p>
                        </div>
                        <Plus className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                  {filteredProducts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No products found matching your criteria
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

export default ProductComparisonPage;
